/**
 * Strips markup and returns the text behind it. Parsed in an inert DOMParser document, since
 * `insertAdjacentHTML` would run `<img src=x onerror=…>` before `textContent` stripped it.
 * @param {string} html input can be markup or a simple string
 * @returns {string} it will return a string value stripped of html
 */
export function sanitizeHtml(html) {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
}

/**
 * Inline formatting an author can wrap around part of a word; every other element
 * (paragraph, list item, line break, image, embed, ...) separates words.
 */
const INLINE_FORMATTING_TAGS = new Set(['A', 'ABBR', 'B', 'CODE', 'EM', 'I', 'MARK', 'S', 'SMALL', 'SPAN', 'STRONG', 'SUB', 'SUP', 'U']);

/**
 * Counts the words in an HTML string the way a reader would: text split on whitespace,
 * with each block-level element (paragraph, list item, heading, image, embed, ...)
 * acting as a boundary, so that "<p>one</p><p>two</p>" is two words, not one.
 * @param {string} html
 * @returns {number}
 */
export function countWords(html) {
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    const parts = [];
    const collect = (node) => {
        for (const child of node.childNodes) {
            if (child.nodeType === Node.TEXT_NODE) {
                parts.push(child.data);
                continue;
            }
            if (child.nodeType !== Node.ELEMENT_NODE) {
                continue;
            }
            const isBoundary = !INLINE_FORMATTING_TAGS.has(child.tagName);
            if (isBoundary) {
                parts.push(' ');
            }
            collect(child);
            if (isBoundary) {
                parts.push(' ');
            }
        }
    };
    collect(body);

    return parts.join('').split(/\s+/).filter(Boolean).length;
}

/**
 * Escapes a string for interpolation into markup, as text content or inside a quoted attribute.
 * sanitizeHtml() returns decoded text, so its result becomes live markup again if it is interpolated
 * rather than escaped.
 * @param {string} text
 * @returns {string}
 */
export function escapeHtml(text) {
    return text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

/**
 * Converts a html string to usable Js element
 * Operation is dangerous in its current state.
 * @param {string} htmlAsString
 * @returns {HTMLElement}
 */
export function dangerouslyConvertHtmlStringToElement(htmlAsString) {
    const template = document.createElement('template');
    htmlAsString = htmlAsString.trim(); // Never return a text node of whitespace as the result
    template.insertAdjacentHTML('afterbegin', htmlAsString);
    return template.firstElementChild;
}
